/**
 * The ZipPass hub — data layer.
 *
 * The hub is the guest's home screen after signup: their standing pass, their
 * balance, whatever is currently in a cloakroom, and how to get to their things.
 * It reads from three existing endpoints and normalises them into one shape, so
 * the component tree renders a single state object rather than juggling four
 * loading flags.
 *
 * Two rules this module exists to enforce:
 *   - Offline must never mean blank. Every successful load is cached with the
 *     time it was taken, so a guest with no signal still sees their pass and
 *     their balance, labelled with how old it is.
 *   - The balance is READ ONLY here. Nothing in this file writes money; the
 *     single debit site is checkout in workers/api.
 */

import { getCheckIns, getGuestProfile, getWalletPass } from './api'

/**
 * Mirrors the API client's (unexported) response envelope. Declared locally so
 * this module can narrow a settled promise without widening api.ts's surface.
 */
interface Envelope<T> {
  data?: T
  error?: { code: string; message: string }
}

/** One item as it appears inside a check-in. */
export interface HubItem {
  id: string
  type: string
  tagNumber: number
  status: string
}

/** A check-in the guest can still collect. */
export interface HubCheckIn {
  id: string
  venueId: string
  venueName: string
  items: HubItem[]
  totalAmount: number
  status: string
  createdAt: string
  checkedOutAt: string | null
}

export interface HubState {
  balance: number
  itemsCheckedIn: number
  /** Check-ins still open at a venue — these are what the guest is carrying. */
  activeCheckIns: HubCheckIn[]
  /** Everything already settled, newest first. The hub shows a slice. */
  recentHistory: HubCheckIn[]
  passId: string | null
  addToWalletUrl: string | null
  /**
   * The guest's STANDING pass payload — the string the staff scanner matches on.
   * Cached with the state, so the hub can render the QR with no signal. Null until
   * the pass row exists.
   */
  qrPayload: string | null
  /** ISO timestamp of the load that produced this state. */
  loadedAt: string
}

/** The subset of the check-ins response the hub cares about. */
export interface CheckInsPayload {
  checkIns: HubCheckIn[]
}

const HISTORY_LIMIT = 3

/**
 * Split a guest's check-ins into what is still at a venue and what is settled.
 *
 * The API returns one list ordered newest-first; the hub needs two different
 * questions answered from it. `status === 'active'` is the only value that means
 * "the guest's items are physically in a cloakroom right now" — anything else
 * (returned, cancelled, expired) belongs in history.
 */
export function splitCheckIns(checkIns: HubCheckIn[] | undefined | null): {
  activeCheckIns: HubCheckIn[]
  recentHistory: HubCheckIn[]
} {
  const list = Array.isArray(checkIns) ? checkIns : []
  // Drop malformed entries once, up front: a bad row must not become an empty
  // card in the hub, and `c?.status !== 'active'` alone would keep `undefined`.
  const valid = list.filter((c): c is HubCheckIn => !!c && typeof c === 'object')
  return {
    activeCheckIns: valid.filter((c) => c.status === 'active'),
    recentHistory: valid.filter((c) => c.status !== 'active').slice(0, HISTORY_LIMIT),
  }
}

/** How many items are still physically checked in, counted from the check-ins. */
export function countActiveItems(activeCheckIns: HubCheckIn[]): number {
  return activeCheckIns.reduce((sum, c) => sum + (Array.isArray(c.items) ? c.items.length : 0), 0)
}

/**
 * Build hub state from the three endpoint payloads.
 *
 * Pure, so it can be tested without a network or a DOM. Every field is optional
 * because a partial failure is normal: the pass endpoint may 404 before a pass
 * exists, and losing it must not blank the balance.
 */
export function buildHubState(
  profile: { balance?: number; itemsCheckedIn?: number } | undefined | null,
  pass: { passId?: string | null; addToWalletUrl?: string | null; qrPayload?: string | null } | undefined | null,
  checkIns: CheckInsPayload | undefined | null,
  loadedAt: string = new Date().toISOString(),
): HubState {
  const { activeCheckIns, recentHistory } = splitCheckIns(checkIns?.checkIns)
  return {
    // The pass endpoint computes balance and item count from the same tables, so
    // either source is authoritative; the profile wins because it also carries
    // the referral code the hub shows.
    balance: profile?.balance ?? 0,
    itemsCheckedIn: activeCheckIns.length > 0 ? countActiveItems(activeCheckIns) : (profile?.itemsCheckedIn ?? 0),
    activeCheckIns,
    recentHistory,
    passId: pass?.passId ?? null,
    addToWalletUrl: pass?.addToWalletUrl ?? null,
    // Only a real payload is worth caching: an empty string would render as a QR
    // that scans into nothing, which is worse than the "no pass yet" state.
    qrPayload: pass?.qrPayload || null,
    loadedAt,
  }
}

// ─── Offline cache ───────────────────────────────────────────
// A cloakroom is a basement with no signal. The last known state is worth more
// than a spinner, as long as the guest can see how stale it is.

const CACHE_KEY = 'zipcheck_hub_cache'

export interface CachedHub {
  state: HubState
  /** True when this came from storage rather than a live load. */
  fromCache: true
}

/** Read the last cached hub state, or null when there is none / it is corrupt. */
export function readHubCache(): HubState | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // A cache written by an older build may not match the current shape; a
    // partial object would render half a screen, so reject anything without the
    // fields the hub cannot function without.
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.balance !== 'number') return null
    if (!Array.isArray(parsed.activeCheckIns)) return null
    return parsed as HubState
  } catch {
    return null
  }
}

export function writeHubCache(state: HubState): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(state))
  } catch {
    /* storage full or blocked — the hub still works, it just cannot be read offline */
  }
}

export function clearHubCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch {
    /* nothing to do */
  }
}

/** Human-readable age of a cached state, for the "as of" label. */
export function describeCacheAge(loadedAt: string, now: Date = new Date()): string {
  const then = new Date(loadedAt).getTime()
  if (Number.isNaN(then)) return 'earlier'
  const minutes = Math.floor((now.getTime() - then) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'yesterday' : `${days} days ago`
}

export interface HubLoadResult {
  state: HubState | null
  /** Set when the live load failed and only cached state is available. */
  fromCache: boolean
  /** Set when neither a live load nor a cache produced anything. */
  error: string | null
}

/**
 * Load the hub: try the network, fall back to the cache, then to an error.
 *
 * Never throws and never returns an empty shell — the caller can always render
 * something and only needs to check `error` to decide whether to show a retry.
 */
export async function loadHub(): Promise<HubLoadResult> {
  // allSettled, not all: a single failing endpoint (the pass call 404s before a
  // pass exists; check-ins can time out) must degrade one card, not blank the
  // hub. `request()` rejects on a transport failure, so a bare `all` would lose
  // every successful call alongside the failed one.
  const [profileRes, passRes, checkInsRes] = await Promise.allSettled([
    getGuestProfile(),
    getWalletPass(),
    getCheckIns(),
  ])

  const settleData = <T>(r: PromiseSettledResult<Envelope<T>>): T | null =>
    r.status === 'fulfilled' && r.value?.data && !r.value.error ? r.value.data : null

  const profile = settleData<{ balance?: number; itemsCheckedIn?: number }>(profileRes)
  const pass = settleData<{ passId?: string | null; addToWalletUrl?: string | null; qrPayload?: string | null }>(
    passRes,
  )
  const checkIns = settleData<CheckInsPayload>(checkInsRes)

  // Without the profile there is no balance and no identity behind the pass.
  // Fall back to the last known state rather than showing an empty hub.
  if (!profile) {
    const cached = readHubCache()
    if (cached) return { state: cached, fromCache: true, error: null }
    const message =
      profileRes.status === 'rejected'
        ? 'Could not reach ZipCheck — check your connection and try again.'
        : 'Could not load your pass.'
    return { state: null, fromCache: false, error: message }
  }

  const state = buildHubState(profile, pass, checkIns)
  writeHubCache(state)
  return { state, fromCache: false, error: null }
}
